import { Component } from '@theme/component';

/**
 * Marquee Announcement Bar custom element that provides continuous scrolling text.
 *
 * @extends {Component}
 */
export class AnnouncementBarMarquee extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#initializeMarquee();
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  #initializeMarquee() {
    const parent = this.parentElement;
    const content = /** @type {HTMLElement} */ (parent?.querySelector('.announcement-bar-marquee__content'));
    if (!content) return;

    const direction = content.dataset.direction || 'left';

    if (direction === 'right') {
      content.style.animationDirection = 'reverse';
    }
  }

  #handleVisibilityChange = () => {
    // Animation will pause/resume automatically based on visibility
  };
}

if (!customElements.get('announcement-bar-marquee-component')) {
  customElements.define('announcement-bar-marquee-component', AnnouncementBarMarquee);
}

const MIN_FILL_WIDTH = 1500;

/** Landing family only — consistent px/s regardless of content length */
const LANDING_PX_PER_SECOND = {
  1: 38,
  2: 52,
  3: 62,
  4: 74,
  5: 90,
};

/** Default fixed durations for homepage and all other non-landing pages */
const SPEED_MAP = {
  1: '40s',
  2: '30s',
  3: '25s',
  4: '20s',
  5: '15s',
};

/**
 * @returns {boolean}
 */
function isLandingFamilyPage() {
  const layout = document.querySelector('#MainContent.content-for-layout, main.content-for-layout');
  if (!layout) return false;

  const classes = layout.className;
  if (classes.includes('hero-product-page')) return true;
  if (classes.includes('pad-vs-panties')) return true;
  if (classes.includes('gopadfree-') && classes.includes('-offer')) return true;

  return false;
}

/**
 * @param {HTMLElement} content
 * @param {string} speed
 * @param {string} direction
 */
function applyMarqueeDuration(content, speed, direction) {
  if (isLandingFamilyPage()) {
    const scrollDistance = content.scrollWidth * 0.5;
    if (!scrollDistance) return;

    const pxPerSecond =
      LANDING_PX_PER_SECOND[/** @type {keyof typeof LANDING_PX_PER_SECOND} */ (Number(speed))] ||
      LANDING_PX_PER_SECOND[2];
    const durationSeconds = Math.max(12, scrollDistance / pxPerSecond);
    const durationValue = `${durationSeconds}s`;

    content.style.setProperty('--marquee-speed', durationValue);
    content.style.animationDuration = durationValue;
  } else {
    const durationValue = SPEED_MAP[/** @type {keyof typeof SPEED_MAP} */ (Number(speed))] || SPEED_MAP[3];
    content.style.setProperty('--marquee-speed', durationValue);
    content.style.animationDuration = durationValue;
  }

  content.style.animationDirection = direction === 'right' ? 'reverse' : 'normal';
}

/**
 * @param {() => void} fn
 * @param {number} wait
 */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(fn, wait);
  };
}

/**
 * @param {HTMLElement} element
 * @returns {number | null}
 */
function parseAnimationDurationSeconds(element) {
  const duration = getComputedStyle(element).animationDuration;
  const match = duration.match(/([\d.]+)s/);
  return match ? parseFloat(match[1]) : null;
}

/**
 * Match top announcement marquee scroll speed to the sticky offers marquee below.
 * Uses pixels-per-second so both bars move at the same visual rate.
 * @returns {boolean}
 */
export function syncAnnouncementBarToStickyMarquee() {
  const stickyWrapper = document.querySelector('.sticky-offers-marquee__wrapper');
  const topContent = document.querySelector('.announcement-bar-marquee__content[data-sync-sticky-speed="true"]');

  if (!stickyWrapper || !topContent) return false;

  const bottomWidth = stickyWrapper.scrollWidth;
  const topWidth = topContent.scrollWidth;
  if (!bottomWidth || !topWidth) return false;

  const bottomDuration = parseAnimationDurationSeconds(stickyWrapper);
  if (!bottomDuration) return false;

  const pixelsPerSecond = (bottomWidth * 0.5) / bottomDuration;
  const topDuration = (topWidth * 0.5) / pixelsPerSecond;

  topContent.style.setProperty('--marquee-speed', `${topDuration}s`);
  topContent.style.animationDuration = `${topDuration}s`;

  const stickyContent = document.querySelector('.sticky-offers-marquee__content');
  if (stickyContent?.dataset.direction === 'right') {
    topContent.style.animationDirection = 'reverse';
  } else {
    topContent.style.animationDirection = '';
  }

  return true;
}

window.syncAnnouncementBarToStickyMarquee = syncAnnouncementBarToStickyMarquee;

/**
 * @param {HTMLElement} content
 * @param {string} originalHTML
 */
function appendOriginalChildren(content, originalHTML) {
  const fragment = document.createElement('div');
  fragment.innerHTML = originalHTML;
  Array.from(fragment.children).forEach((child) => {
    content.appendChild(child);
  });
}

/**
 * Repeat announcements until the track fills the viewport, then duplicate for a seamless -50% loop.
 * @param {HTMLElement} element
 */
function buildAnnouncementMarquee(element) {
  const container = /** @type {HTMLElement | null} */ (element.querySelector('.announcement-bar-marquee__container'));
  const content = /** @type {HTMLElement | null} */ (element.querySelector('.announcement-bar-marquee__content'));
  if (!container || !content) return;

  if (!element._abmOriginalHTML) {
    element._abmOriginalHTML = content.innerHTML.trim();
  }

  const originalHTML = element._abmOriginalHTML;
  if (!originalHTML) return;

  content.innerHTML = originalHTML;

  const minWidth = Math.max(container.offsetWidth, MIN_FILL_WIDTH);
  const setWidth = content.scrollWidth;
  if (setWidth === 0) return;

  const copiesNeeded = Math.max(1, Math.ceil(minWidth / setWidth));
  for (let i = 1; i < copiesNeeded; i++) {
    appendOriginalChildren(content, originalHTML);
  }

  const filledHTML = content.innerHTML;
  const loopFragment = document.createElement('div');
  loopFragment.innerHTML = filledHTML;
  Array.from(loopFragment.children).forEach((child) => {
    content.appendChild(child);
  });

  const speed = content.dataset.speed || (isLandingFamilyPage() ? '2' : '3');
  const direction = content.dataset.direction || 'left';
  applyMarqueeDuration(content, speed, direction);

  content.dataset.duplicated = 'true';
  void content.offsetWidth;
}

/**
 * @param {HTMLElement} element
 * @param {{ force?: boolean }} [options]
 */
function initAnnouncementMarquee(element, options = {}) {
  const { force = false } = options;

  if (element._abmInitialized && !force) return;

  if (force) {
    delete element._abmOriginalHTML;
  }

  element._abmInitialized = true;

  buildAnnouncementMarquee(element);

  if (!element.querySelector('announcement-bar-marquee-component')) {
    const component = document.createElement('announcement-bar-marquee-component');
    element.appendChild(component);
  }

  if (!element._abmResizeObserver) {
    const container = element.querySelector('.announcement-bar-marquee__container');
    if (container) {
      const onResize = debounce(() => buildAnnouncementMarquee(element), 150);
      const observer = new ResizeObserver(onResize);
      observer.observe(container);
      element._abmResizeObserver = observer;
    }
  }
}

function scheduleAnnouncementBarSpeedSync() {
  const attemptSync = () => {
    if (typeof window.syncAnnouncementBarToStickyMarquee === 'function') {
      window.syncAnnouncementBarToStickyMarquee();
    }
  };

  attemptSync();
  requestAnimationFrame(attemptSync);
  setTimeout(attemptSync, 100);
  setTimeout(attemptSync, 500);
}

function setupMarquees() {
  document.querySelectorAll('.announcement-bar-marquee').forEach((element) => {
    initAnnouncementMarquee(/** @type {HTMLElement} */ (element));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupMarquees);
} else {
  setupMarquees();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = /** @type {HTMLElement | undefined} */ (event.target);
  section?.querySelectorAll('.announcement-bar-marquee').forEach((element) => {
    initAnnouncementMarquee(/** @type {HTMLElement} */ (element), { force: true });
  });
});
